import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './UpgradePromptDialog.css';
import './Button.css';
import StorageMeter from './StorageMeter';
import { UPSELL_PROMPTS, UPSELL_ACTIONS, resolveUnlockTier, buildUpsellCopy } from '../upsell/triggers';
import { startSubscriptionCheckout } from '../pricing/checkout';
import { rememberUpsellSource, UPSELL_FLOWS } from '../upsell/attribution';
import { isSubscriber } from '../pricing/subscription';
import { trackEvent } from '../analytics';

// The one upgrade prompt behind all three touchpoints (#350): the locked
// watermark switch, the full storage pool, and an empty credit balance.
//
// One component rather than three, because the three differ only in copy, which
// remedy is offered alongside the upgrade, and whether the storage meter is
// shown — while the parts that MUST NOT diverge are shared: naming the real
// unlock tier (never a hardcoded "Solo"), routing subscribers to the portal
// instead of a checkout the server rejects, and emitting the funnel events with
// the same property names from every touchpoint.
//
// `promptId` is the GA4 dimension value, so it is also the switch key.
function UpgradePromptDialog({
  promptId,
  // Effective entitlements (useEntitlements / useBillingSummary). Drives both
  // the unlock-tier resolution and `from_tier` on every event.
  entitlements,
  // Raw Stripe state. Needed because a lapsed or past_due-past-grace account
  // reads as tier `free` while create_subscription_checkout still refuses it.
  subscription,
  // Storage variant.
  usedBytes,
  totalBytes,
  onFreeUpSpace,
  // Credits variant.
  shortBy,
  onTopUp,
  // Attribution only — rides along on the events.
  projectId,
  onClose,
  // Injectable for tests.
  checkout = startSubscriptionCheckout,
}) {
  const tier = (entitlements && entitlements.tier) || 'free';
  const monthlyCredits = (entitlements && entitlements.monthlyCredits) || 0;
  const unlockTier = resolveUnlockTier(promptId, {
    poolBytes: totalBytes,
    monthlyCredits,
  });
  const copy = buildUpsellCopy(promptId, unlockTier, { shortBy });

  const [pending, setPending] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // An existing Stripe relationship means plan changes belong in the portal —
  // create_subscription_checkout rejects a second subscription outright, so
  // offering a live checkout here would be a button that cannot succeed.
  const mustUsePortal = isSubscriber(subscription);
  const canUpgrade = Boolean(unlockTier);

  const baseParams = {
    prompt_id: promptId,
    from_tier: tier,
    unlock_tier: unlockTier ? unlockTier.id : null,
    ...(projectId ? { project_id: projectId } : {}),
  };
  // Params are rebuilt every render; the shown-effect must not re-fire because
  // of that, so it reads them through a ref and depends only on the prompt id.
  const paramsRef = useRef(baseParams);
  paramsRef.current = baseParams;

  // Exactly one `upsell_prompt_shown` per opening of the prompt. Mounting is
  // the impression: the parents mount this conditionally, so a re-open is a
  // fresh mount and a re-render is not.
  useEffect(() => {
    trackEvent('upsell_prompt_shown', paramsRef.current);
  }, [promptId]);

  const clicked = (action) => trackEvent('upsell_prompt_clicked', { ...baseParams, action });

  const handleUpgrade = async () => {
    if (pending || !unlockTier) return;
    clicked(UPSELL_ACTIONS.UPGRADE);
    setCheckoutError(null);
    setPending(true);
    // startSubscriptionCheckout owns `subscribe_checkout_started` and the
    // redirect; it resolves only when the checkout could not be started.
    const { error } = await checkout({
      planId: unlockTier.planId,
      source: promptId,
      fromTier: tier,
    });
    if (error) {
      setCheckoutError(error);
      setPending(false);
    }
  };

  // Params are rebuilt every render, so the Escape effect reads them through
  // the same ref the shown-effect uses rather than re-binding the listener.
  const handleDismiss = useCallback(() => {
    trackEvent('upsell_prompt_dismissed', paramsRef.current);
    if (onClose) onClose();
  }, [onClose]);

  // This prompt stacks on top of RenderOptionsDialog, whose own Escape handler
  // deliberately bails while a prompt is open so the render dialog is not torn
  // down underneath it. Without a listener here Escape would do nothing at all
  // on an aria-modal dialog. Dismissing through handleDismiss (not onClose)
  // keeps `upsell_prompt_dismissed` firing, so Escape-dismissals stay in the
  // funnel instead of inflating the conversion rate of shown prompts.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleDismiss]);

  const handleFreeUpSpace = () => {
    clicked(UPSELL_ACTIONS.FREE_UP_SPACE);
    if (onFreeUpSpace) onFreeUpSpace();
    else if (onClose) onClose();
  };

  const handleTopUp = () => {
    clicked(UPSELL_ACTIONS.TOP_UP);
    // The top-up completes on Stripe and lands on /purchase-success, so — as
    // with startSubscriptionCheckout — the touchpoint has to be stashed now or
    // `topup_purchased` has nothing to attribute itself to. Under the top-up
    // key: this click is not evidence of a subscription, and the activation
    // hook must not be able to read it back as one.
    rememberUpsellSource(promptId, UPSELL_FLOWS.TOPUP);
    if (onTopUp) onTopUp();
  };

  const isStorage = promptId === UPSELL_PROMPTS.STORAGE_POOL_FULL;
  const isCredits = promptId === UPSELL_PROMPTS.CREDITS_EXHAUSTED;

  return (
    <div
      className="dialog-overlay upgrade-prompt-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) handleDismiss(); }}
      role="presentation"
    >
      <div
        className="dialog-content upgrade-prompt"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-prompt-title"
        data-testid="upgrade-prompt"
        data-prompt-id={promptId}
      >
        <h3 id="upgrade-prompt-title" data-testid="upgrade-prompt-title">{copy.title}</h3>
        <p className="upgrade-prompt__body" data-testid="upgrade-prompt-body">{copy.body}</p>

        {/* Hint copy comes from StorageMeter's default so this modal and the
            account page never describe the same pool differently. */}
        {isStorage && (
          <StorageMeter
            usedBytes={usedBytes}
            totalBytes={totalBytes}
            testId="upgrade-prompt-storage-meter"
          />
        )}

        {canUpgrade && (
          <div className="upgrade-prompt__tier" data-testid="upgrade-prompt-tier">
            <div className="upgrade-prompt__tier-head">
              <span className="upgrade-prompt__tier-name">{unlockTier.name}</span>
              <span className="upgrade-prompt__tier-price">
                {unlockTier.priceLabel}
                <span className="upgrade-prompt__tier-suffix">{unlockTier.priceSuffix}</span>
              </span>
            </div>
            <ul className="upgrade-prompt__tier-features">
              {unlockTier.features.slice(0, 3).map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          </div>
        )}

        {checkoutError && (
          <p className="upgrade-prompt__error" role="alert" data-testid="upgrade-prompt-error">
            {checkoutError}
          </p>
        )}

        <div className="dialog-buttons upgrade-prompt__actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleDismiss}
            data-testid="upgrade-prompt-dismiss"
          >
            Not now
          </button>

          {isStorage && (
            <button
              type="button"
              className="btn btn-secondary upgrade-prompt__alt"
              onClick={handleFreeUpSpace}
              data-testid="upgrade-prompt-free-space"
            >
              Free up space
            </button>
          )}

          {isCredits && (
            <Link
              to="/credits"
              className="btn btn-secondary upgrade-prompt__alt"
              onClick={handleTopUp}
              data-testid="upgrade-prompt-topup"
            >
              Buy credits
            </Link>
          )}

          {/* No self-serve tier improves on what they have (Pro, or a
              grandfathered allowance above the catalog) — offering an "upgrade"
              here would sell a fix that does not exist, or one that shrinks
              them. */}
          {canUpgrade && (mustUsePortal ? (
            <Link
              to="/account"
              className="btn btn-teal"
              onClick={() => clicked(UPSELL_ACTIONS.MANAGE_BILLING)}
              data-testid="upgrade-prompt-portal"
            >
              Change plan
            </Link>
          ) : (
            <button
              type="button"
              className="btn btn-teal"
              onClick={handleUpgrade}
              disabled={pending}
              data-testid="upgrade-prompt-upgrade"
            >
              {pending ? 'Opening…' : `Upgrade to ${unlockTier.name}`}
            </button>
          ))}
        </div>

        <p className="upgrade-prompt__foot">
          <Link
            to="/pricing"
            onClick={() => clicked(UPSELL_ACTIONS.VIEW_PLANS)}
            data-testid="upgrade-prompt-view-plans"
          >
            Compare all plans
          </Link>
        </p>
      </div>
    </div>
  );
}

export default UpgradePromptDialog;
